import { cn } from "@u/cn";
import { chipVariants } from "@ui/chip-variants";
import type { ReactNode } from "react";

/**
 * Quiet segmented control — the venue-editor's shared single/multi-select chip
 * language (subtle tint + ring when active, never a loud primary fill). Single
 * source for the pattern that was hand-rolled in DesignTab, MenuTab currency,
 * BookingTab mode and the button picker.
 */
export function SegChip({
  active,
  onClick,
  disabled,
  multi,
  size = "md",
  className,
  children,
}: {
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
  /** Multi-select (checkbox) vs single-select (radio, default). Sets a11y role. */
  multi?: boolean;
  size?: "sm" | "md";
  className?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role={multi ? "checkbox" : "radio"}
      aria-checked={active}
      disabled={disabled}
      onClick={onClick}
      className={cn(chipVariants({ size, active }), className)}
    >
      {children}
    </button>
  );
}

/**
 * Labeled group wrapping a set of `SegChip`s. `layout="fill"` = equal-width row
 * (2-3 options); `layout="wrap"` = flowing grid (many options). `multi` flips
 * the a11y role to `group` (checkboxes) from `radiogroup`.
 */
export function SegGroup({
  label,
  multi,
  layout = "wrap",
  className,
  children,
}: {
  label: string;
  multi?: boolean;
  layout?: "fill" | "wrap";
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      role={multi ? "group" : "radiogroup"}
      aria-label={label}
      className={cn(
        layout === "fill"
          ? "flex gap-2 [&>button]:flex-1"
          : "flex flex-wrap gap-2",
        className,
      )}
    >
      {children}
    </div>
  );
}
